import React from 'react';

import UserPipelineDebugLoader from './UserPipelineDebugLoader';
import { useJobProspectsStatsState, useJobProspectsStatsActions } from './JobProspectsStatsContext';
import PageTitle from '../../Shared/UI/PageTitle/components/PageTitle';
import Stack from '../../Shared/UI/Stack/components/Stack';
import Field from '../../Shared/UI/Field/components/Field';
import Select from '../../Shared/UI/Select/components/Select';
import Link from '../../Shared/UI/Link/components/Link';
import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Sidebar from '../../Shared/UI/Sidebar/components/Sidebar';
import SidebarItem from '../../Shared/UI/Sidebar/components/SidebarItem';
import { getUserPipelineDebug, generateMatchScoresForUser } from './restApi';
import Button from '../../Shared/UI/Button/components/Button';
import styles from './UserPipelineDebug.module.scss';

const UserPipelineDebugContent = () => {
  const state = useJobProspectsStatsState();
  const { onResponse, onError } = useJobProspectsStatsActions();

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    if (!userId) return;
    onResponse({ loading: true });
    getUserPipelineDebug({ userId, onResponse, onError });
  };

  if (state?.loading) return <p>Loading...</p>;

  if (state?.feedbackVariant === 'error') {
    return <p style={{ color: 'var(--color-status-error-text)' }}>Error: {state?.feedbackMessage}</p>;
  }

  if (!state?.users) return null;

  return (
    <Stack gap="lg">
      <Field label="Select Beta Test User">
        <Select
          value={state?.user?.id || ''}
          onChange={handleUserSelect}
        >
          <option value="">-- Select a user --</option>
          {state.users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </Select>
      </Field>

      {state?.user && state?.pipeline && (
        <>
          <PipelineStages pipeline={state.pipeline} matchScores={state.match_scores} userId={state.user.id} onResponse={onResponse} onError={onError} />
          <MatchScoreStats matchScores={state.match_scores} />
          <TopScores scores={state.match_scores?.top_scores} />
          <ProspectsSummary prospects={state.job_prospects} />
          <RecentProspects prospects={state.job_prospects?.recent} />
        </>
      )}
    </Stack>
  );
};

const UserPipelineDebug = ({ userId }) => {
  const state = useJobProspectsStatsState();

  const selectedUserId = state?.user?.id || userId;
  const basePath = '/job_prospects_stats/user_pipeline_debug';
  const scoresPath = selectedUserId
    ? `/job_prospects_stats/user_match_scores?user_id=${selectedUserId}`
    : null;

  return (
    <>
      <UserPipelineDebugLoader userId={userId} />
      <section className={styles.container}>
        <div className={styles.body}>
          <Sidebar aria-label="User pipeline navigation">
            <SidebarItem href="/job_prospects_stats">← Stats Overview</SidebarItem>
            <SidebarItem href={basePath} active>Overview</SidebarItem>
            {scoresPath && <SidebarItem href={scoresPath}>Match Scores</SidebarItem>}
          </Sidebar>
          <div className={styles.content}>
            <div className={styles.pageLayout}>
              <Stack gap="lg">
                <PageTitle text="User Pipeline Debug" />
                <UserPipelineDebugContent />
              </Stack>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const PipelineStages = ({ pipeline, matchScores, userId, onResponse, onError }) => (
  <div>
    <h3 className={styles.sectionHeading}>Pipeline Stage Breakdown</h3>
    <Table>
      <Thead>
        <Tr>
          <Th>Stage</Th>
          <Th>Count</Th>
          <Th>Action</Th>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>Job descriptions waiting for line importance analysis</Td>
          <Td>{pipeline.raw_copy_cached}</Td>
          <Td></Td>
        </Tr>
        <Tr>
          <Td>Job descriptions with one failed analysis attempt</Td>
          <Td>{pipeline.one_failed_analysis_attempt}</Td>
          <Td></Td>
        </Tr>
        <Tr>
          <Td>Job descriptions with failed line importance analysis</Td>
          <Td>{pipeline.analysis_failed}</Td>
          <Td></Td>
        </Tr>
        <Tr>
          <Td>Job descriptions waiting to be scored for this user</Td>
          <Td>{pipeline.awaiting_scoring}</Td>
          <Td>
            <Button
              size="sm"
              onClick={() => generateMatchScoresForUser({ userId, onResponse, onError })}
            >
              Score Job Descriptions
            </Button>
          </Td>
        </Tr>
        <Tr>
          <Td>Total match scores for this user</Td>
          <Td>{matchScores?.total}</Td>
          <Td></Td>
        </Tr>
        <Tr>
          <Td>New match scores (last 7 days)</Td>
          <Td>{matchScores?.recent_7_days}</Td>
          <Td></Td>
        </Tr>
        <Tr>
          <Td>Scores above prospect threshold</Td>
          <Td>{matchScores?.above_threshold}</Td>
          <Td></Td>
        </Tr>
      </Tbody>
    </Table>
  </div>
);

const MatchScoreStats = ({ matchScores }) => (
  <div>
    <h3 className={styles.sectionHeading}>Match Score Statistics</h3>
    <Table>
      <Thead>
        <Tr>
          <Th>Metric</Th>
          <Th>Value</Th>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>Mean</Td>
          <Td>{matchScores?.mean ?? 'N/A'}</Td>
        </Tr>
        <Tr>
          <Td>Std Dev</Td>
          <Td>{matchScores?.std_dev ?? 'N/A'}</Td>
        </Tr>
        <Tr>
          <Td>Threshold (mean + 1.0 × std dev)</Td>
          <Td>{matchScores?.threshold ?? 'N/A'}</Td>
        </Tr>
        <Tr>
          <Td>Above threshold</Td>
          <Td>{matchScores?.above_threshold}</Td>
        </Tr>
        <Tr>
          <Td>Below threshold</Td>
          <Td>{matchScores?.below_threshold}</Td>
        </Tr>
        <Tr>
          <Td>Blocked (unmet required)</Td>
          <Td>{matchScores?.blocked_count}</Td>
        </Tr>
        <Tr>
          <Td>Soft penalty (not blocked)</Td>
          <Td>{matchScores?.soft_penalty_count}</Td>
        </Tr>
      </Tbody>
    </Table>
  </div>
);

const TRUNCATE_AT = 80;

const truncate = (value) => {
  if (value == null) return '';
  const str = String(value);
  return str.length > TRUNCATE_AT ? str.slice(0, TRUNCATE_AT) : str;
};

const GapBadge = ({ blocked, softPenaltyCount }) => {
  if (blocked) {
    return <span className={styles.badgeBlocked}>Blocked</span>;
  }
  if (softPenaltyCount > 0) {
    return <span className={styles.badgePenalty}>Penalty</span>;
  }
  return null;
};

const GapsCell = ({ unmetRequired }) => {
  const items = unmetRequired || [];
  if (items.length === 0) {
    return '—';
  }

  return (
    <>
      {items.map((item, index) => {
        const fullText = item?.text == null ? '' : String(item.text);
        return (
          <span key={index} className={styles.gapLine} title={fullText}>
            {`Missing required: ${truncate(fullText)}`}
          </span>
        );
      })}
    </>
  );
};

const TopScores = ({ scores }) => {
  if (!scores || scores.length === 0) return null;

  return (
    <div>
      <h3 className={styles.sectionHeading}>Top 10 Match Scores</h3>
      <Table>
        <Thead>
          <Tr>
            <Th>Score</Th>
            <Th>Gaps</Th>
            <Th>Title</Th>
            <Th>Company</Th>
            <Th>Link</Th>
            <Th>Scored At</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {scores.map((score, i) => (
            <Tr key={i}>
              <Td>
                <span className={score.above_threshold ? styles.scoreAboveThreshold : styles.scoreBelowThreshold}>
                  {score.match_score}
                </span>
                <GapBadge blocked={score.blocked} softPenaltyCount={score.soft_penalty_count} />
              </Td>
              <Td>
                <GapsCell unmetRequired={score.unmet_required} />
              </Td>
              <Td>{score.title || '—'}</Td>
              <Td>{score.company_name || '—'}</Td>
              <Td>
                {score.source_url ? (
                  <Link href={score.source_url} external size="sm">View</Link>
                ) : '—'}
              </Td>
              <Td>{score.created_at}</Td>
              <Td>{score.closed ? 'Closed' : 'Open'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

const ProspectsSummary = ({ prospects }) => {
  if (!prospects) return null;
  const byStatus = prospects.by_status;

  return (
    <div>
      <h3 className={styles.sectionHeading}>Job Prospects ({prospects.total} total)</h3>
      <div className={styles.statList}>
        {Object.entries(byStatus).map(([status, count]) => (
          count > 0 && <p key={status}><span className={styles.statLabel}>{formatStatus(status)}:</span> {count}</p>
        ))}
        {Object.values(byStatus).every(c => c === 0) && <p className={styles.emptyState}>No job prospects</p>}
      </div>
    </div>
  );
};

const RecentProspects = ({ prospects }) => {
  if (!prospects || prospects.length === 0) return null;

  return (
    <div>
      <h3 className={styles.sectionHeading}>Recent 10 Prospects</h3>
      <Table>
        <Thead>
          <Tr>
            <Th>Title</Th>
            <Th>Company</Th>
            <Th>Link</Th>
            <Th>Status</Th>
            <Th>Created At</Th>
          </Tr>
        </Thead>
        <Tbody>
          {prospects.map((prospect, i) => (
            <Tr key={i}>
              <Td>{prospect.title || '—'}</Td>
              <Td>{prospect.company_name || '—'}</Td>
              <Td>
                {prospect.source_url ? (
                  <Link href={prospect.source_url} external size="sm">View</Link>
                ) : '—'}
              </Td>
              <Td>{formatStatus(prospect.status)}</Td>
              <Td>{prospect.created_at}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

const formatStatus = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default UserPipelineDebug;
